import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { map, Subscription, switchMap } from 'rxjs';
import { ImpactService } from './impact.service';
import { Transport } from 'src/app/trip-calculator/models/transport';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import {
  AlternativeChoice,
  AlternativeMode,
  ApplyTipsTarget,
  DirectFlightTipData,
  EcoAccommodationTipData,
  HighEmissionActivityTipData,
  ReductionTip,
  TipSection,
  TransportAlternativeRoute,
  TransportAlternativeTipData,
} from '../models/reduction-tip';
import { TripCommitment } from 'src/app/shared/model/reduction-action';
import {
  AlternativeSegment,
  applyReductionTips,
  CARPOOLING_PASSENGERS,
} from '../utils/apply-reduction-tips';
import {
  bestSavingPerElement,
  getTipSaving,
  simulateSavings,
  sumSavingsByOverviewType,
  TipSavingContext,
  updateTipSelection,
} from '../utils/reduction-tip-savings';
import { TIP_SECTION } from '../constants/reduction-tip-sections';
import { TRIP_OBJECTIVES, TripObjective } from '../constants/trip-objectives';

/** A commitment of the company, and what breaks it on this trip. */
export interface CommitmentDetail extends TripCommitment {
  /** Trip elements the matching tips were raised on — empty unless improvable. */
  elements: string[];
}

/** The trip measured against one of the company's objectives. */
export interface ObjectiveComparison extends TripObjective {
  target: number;
  value: number;
  isAboveTarget: boolean;
  /** Distance to the target, in % of it — always positive. */
  differencePercent: number;
}

/**
 * State of the reduction tips shown across the impact page — the tips cards
 * under each stats category, the routes dialog, the simulation panel, the
 * apply dialog and the objectives section. Every one of them reads it
 * directly rather than receiving it through inputs, so a row can select
 * itself without the page forwarding handlers.
 */
@Injectable({
  providedIn: 'root',
})
export class ReductionTipsService {
  private impactService = inject(ImpactService);
  private tripService = inject(TripService);
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private currentUserService = inject(CurrentUserService);
  private formatCo2e = inject(FormatCo2eService);
  private router = inject(Router);

  tips = signal<ReductionTip[]>([]);
  commitments = signal<TripCommitment[]>([]);
  isLoading = signal(true);
  /** A save is in flight — the apply button must not fire twice. */
  isApplying = signal(false);

  /**
   * Tips the user simulates, and the mode and itinerary picked for transport
   * tips. One choice per tip rather than a mode and an index side by side: an
   * itinerary index only means something within its mode.
   */
  selectedTipIds = signal<ReadonlySet<string>>(new Set<string>());
  alternativeChoiceByTipId = signal<Record<string, AlternativeChoice>>({});

  /**
   * Tips cards unfolded by the user. Folded by default, to keep the report
   * short; "see the tips" in the simulation panel unfolds them all.
   */
  openTipSections = signal<ReadonlySet<TipSection>>(new Set<TipSection>());

  /** The tips request in flight, dropped when another trip is asked for. */
  private loadSubscription?: Subscription;

  /** Transport tip and mode whose itineraries the routes dialog shows, if open. */
  routesDialog = signal<{ tipId: string; mode: AlternativeMode } | null>(null);

  /** The dialog asking whether the tips overwrite the trip or go to a copy. */
  applyDialogVisible = signal(false);

  /** Every figure is displayed per traveller when true. */
  readonly perPerson = this.impactService.perPerson;

  /** Travellers of the loaded trip, never below one — every per-person conversion divides by it. */
  readonly travellers = computed(() =>
    Math.max(1, this.impactService.trip()?.travellers ?? 1),
  );

  private readonly savingContext = computed<TipSavingContext>(() => ({
    travellers: this.travellers(),
    transportCategoryOf: (transportType: string) =>
      this.impactService.getTransportCategory(transportType) as
        | string
        | undefined,
  }));

  /** Tips grouped under the stats category they are shown in. */
  readonly tipsBySection = computed(() => {
    const bySection: Partial<Record<TipSection, ReductionTip[]>> = {};
    this.tips().forEach((tip) => {
      (bySection[TIP_SECTION[tip.type]] ??= []).push(tip);
    });
    return bySection;
  });

  readonly hasSelection = computed(() => this.selectedTipIds().size > 0);

  /** kg CO₂e avoided per breakdown category by the selected tips. */
  readonly savingsByType = computed<Record<string, number>>(() => {
    const selected = this.selectedTipIds();
    const choices = this.alternativeChoiceByTipId();
    const ctx = this.savingContext();

    return sumSavingsByOverviewType(
      this.tips()
        .filter((tip) => selected.has(tip.id))
        .map((tip) => getTipSaving(tip, ctx, choices[tip.id])),
    );
  });

  /** The trip once the selected tips are taken — null before the report loads. */
  readonly simulation = computed(() => {
    const overview = this.impactService.overview();
    return overview ? simulateSavings(overview, this.savingsByType()) : null;
  });

  /** What the selected tips avoid, formatted on the basis the page displays. */
  readonly formattedAvoided = computed(() =>
    this.formatCo2e.format(this.toDisplayed(this.simulation()?.avoided ?? 0), {
      perPerson: this.perPerson(),
    }),
  );

  /**
   * The trip if every tip were taken, each flight on its best alternative —
   * what the page announces as "up to" before anything is ticked.
   */
  readonly potential = computed(() => {
    const overview = this.impactService.overview();
    if (!overview) return null;

    const ctx = this.savingContext();
    return simulateSavings(
      overview,
      sumSavingsByOverviewType(
        bestSavingPerElement(this.tips().map((tip) => getTipSaving(tip, ctx))),
      ),
    );
  });

  /** The company's active objectives that one trip can be measured against. */
  private readonly activeTripObjectives = computed(() => {
    const objectives = this.currentUserService.objectives();
    return TRIP_OBJECTIVES.flatMap((definition) => {
      const target = objectives.find(
        (o) => o.isActive && o.type === definition.type,
      )?.targetValue;
      return target ? [{ ...definition, target }] : [];
    });
  });

  /** The trip's total as saved, kg CO₂e — what every objective is measured on. */
  readonly tripTotal = computed(
    () => this.impactService.trip()?.tripImpact?.totalImpact ?? null,
  );

  /** Each commitment with the trip elements that break it. */
  readonly commitmentDetails = computed<CommitmentDetail[]>(() =>
    this.commitments().map((commitment) => ({
      ...commitment,
      elements:
        commitment.status === 'improvable'
          ? this.tips()
              .filter((tip) => tip.type === commitment.action)
              .map((tip) => this.elementLabelOf(tip))
              .filter((label): label is string => label !== null)
          : [],
    })),
  );

  /** Trip totals, the basis every saving is computed on, as the page displays them. */
  toDisplayed(total: number): number {
    return this.perPerson() ? total / this.travellers() : total;
  }

  /** Per-traveller figures from the API, as the page displays them. */
  fromPerPerson(perPerson: number): number {
    return this.perPerson() ? perPerson : perPerson * this.travellers();
  }

  /**
   * Every active objective against the trip at `totalImpact` kg CO₂e — its
   * saved total, or what is left once tips are taken. An objective the trip
   * cannot be measured on (per € without a price) is left out.
   */
  compareToObjectives(totalImpact: number): ObjectiveComparison[] {
    const trip = this.impactService.trip();
    const basis = {
      travellers: this.travellers(),
      days: Math.max(1, trip?.days ?? 1),
      price: trip?.price ?? null,
    };
    return this.activeTripObjectives().flatMap((objective) => {
      const value = objective.measure(totalImpact, basis);
      if (value === null) return [];
      const { target } = objective;
      return [
        {
          ...objective,
          value,
          isAboveTarget: value > target,
          differencePercent: Math.abs(
            Math.round(((value - target) / target) * 100),
          ),
        },
      ];
    });
  }

  /**
   * The state is root-scoped, so a new trip starts from a clean simulation —
   * and without the previous tips: they belong to another trip, or, once
   * applied, to rows the save recreated. A request still in flight for them
   * is dropped, or a slow answer would bring them back.
   */
  load(tripId: string): void {
    this.loadSubscription?.unsubscribe();
    this.isLoading.set(true);
    this.tips.set([]);
    this.commitments.set([]);
    this.selectedTipIds.set(new Set<string>());
    this.alternativeChoiceByTipId.set({});
    this.routesDialog.set(null);
    this.impactService.getReductionTips(tripId).subscribe({
      next: (response) => {
        this.tips.set(response.tips);
        this.commitments.set(response.commitments ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  isSelected(tipId: string): boolean {
    return this.selectedTipIds().has(tipId);
  }

  choiceOf(tipId: string): AlternativeChoice | undefined {
    return this.alternativeChoiceByTipId()[tipId];
  }

  /** Saving of one tip over the whole trip, on the alternative in use. */
  savingOf(tip: ReductionTip): number {
    return getTipSaving(tip, this.savingContext(), this.choiceOf(tip.id))
      .savings;
  }

  setSelection(tipIds: readonly string[], selected: boolean): void {
    this.selectedTipIds.update((current) =>
      updateTipSelection(current, this.tips(), tipIds, selected),
    );
  }

  clearSelection(): void {
    this.selectedTipIds.set(new Set<string>());
  }

  toggleTipSection(section: TipSection): void {
    this.openTipSections.update((current) => {
      const next = new Set(current);
      if (!next.delete(section)) next.add(section);
      return next;
    });
  }

  openAllTipSections(): void {
    this.openTipSections.set(
      new Set(Object.keys(this.tipsBySection()) as TipSection[]),
    );
  }

  openRoutes(tipId: string, mode: AlternativeMode): void {
    this.routesDialog.set({ tipId, mode });
  }

  closeRoutes(): void {
    this.routesDialog.set(null);
  }

  openApplyDialog(): void {
    this.applyDialogVisible.set(true);
  }

  closeApplyDialog(): void {
    this.applyDialogVisible.set(false);
  }

  /** Picking a mode on the page or an itinerary in the dialog means simulating it. */
  chooseAlternative(tipId: string, choice: AlternativeChoice): void {
    this.alternativeChoiceByTipId.update((choices) => ({
      ...choices,
      [tipId]: choice,
    }));
    this.setSelection([tipId], true);
    this.closeRoutes();
  }

  /**
   * What a tip is about, as the trip names it: the leg's own title, else the
   * one the tip carries, else its translated type. `null` for `trip_duration`,
   * which is about no element.
   *
   * A direct-flight tip carries no title, so the leg is looked up on the trip.
   */
  elementLabelOf(tip: ReductionTip): string | null {
    switch (tip.type) {
      case 'transport_alternative':
      case 'direct_flight': {
        const data = tip.data as
          | TransportAlternativeTipData
          | DirectFlightTipData;
        const title =
          this.impactService
            .trip()
            ?.transports?.find((t) => t.id === data.transportId)
            ?.title?.trim() ||
          ('transportTitle' in data ? data.transportTitle : undefined);
        return title || this.translate(`categoryType.${data.transportType}`);
      }
      case 'eco_accommodation': {
        const data = tip.data as EcoAccommodationTipData;
        return (
          data.accommodationTitle ||
          this.translate(`categoryType.${data.accommodationType}`)
        );
      }
      case 'high_emission_activity': {
        const data = tip.data as HighEmissionActivityTipData;
        return (
          data.activityTitle ||
          this.translate(`categoryType.${data.activityType}`)
        );
      }
      default:
        return null;
    }
  }

  /**
   * What names an alternative: the vehicle of a confirmed itinerary ("TGV"),
   * except `mixed` and `carpooling`, which are named after the mode — their
   * vehicle alone would hide the change of mode or the sharing.
   */
  alternativeLabel(
    mode: AlternativeMode,
    route: TransportAlternativeRoute | null,
  ): string {
    if (mode === 'carpooling') {
      return this.translate(
        'reductionTips.transportAlternative.carpoolingWithOccupancy',
        { count: CARPOOLING_PASSENGERS },
      );
    }
    if (!route || mode === 'mixed') {
      return this.translate(`reductionTips.transportAlternative.${mode}`);
    }
    return this.translate(`categoryType.${route.transportType}`);
  }

  /**
   * "Direct", "1 correspondance", "2 correspondances" — abbreviated when
   * `short`, to fit a choice chip: "1 corresp.".
   */
  changesLabel(route: TransportAlternativeRoute, short = false): string {
    if (route.changes <= 0) return this.translate('reductionTips.route.direct');
    const key =
      (route.changes === 1 ? 'change' : 'changes') + (short ? 'Short' : '');
    return this.translate(`reductionTips.route.${key}`, {
      count: route.changes,
    });
  }

  /** The tips written to the trip — at most one per element, see `updateTipSelection()`. */
  private readonly appliedTips = computed(() =>
    this.tips().filter((tip) => this.isSelected(tip.id)),
  );

  /**
   * Name given to a leg replaced by an alternative: "Train (alternative to
   * <what it was>)", falling back on the translated transport type when the
   * leg was never named. The mode chosen comes first — a trip's transport list
   * is read for what each leg now is, the flight it replaces being the context.
   */
  private alternativeTitleOf(
    transport: Transport,
    mode: AlternativeMode,
    segment?: AlternativeSegment,
  ): string {
    // A confirmed itinerary becomes one transport per stretch, and the list is
    // then read stretch by stretch: what each one is and where it runs says
    // more than the flight it descends from, which the others already repeat.
    if (segment) {
      return this.translate('reductionTips.alternativeSegmentTitle', {
        mode: this.translate(`categoryType.${segment.transportType}`),
        from: segment.departureStop,
        to: segment.arrivalStop,
      });
    }

    const current =
      transport.title?.trim() ||
      this.translate(`categoryType.${transport.type}`);

    return this.translate('reductionTips.alternativeTitle', {
      mode: this.translate(`reductionTips.transportAlternative.${mode}`),
      title: this.originalTitleOf(current),
    });
  }

  /**
   * The leg's own name, freed from the wording a previous apply wrapped it in
   * — renaming twice must not nest it. Only what surrounds the original name
   * is stable across modes, so it is read off the pattern itself rather than
   * spelled out once per language.
   */
  private originalTitleOf(title: string): string {
    const MODE_SLOT = '\u0000';
    const TITLE_SLOT = '\u0001';
    const parts = this.translate('reductionTips.alternativeTitle', {
      mode: MODE_SLOT,
      title: TITLE_SLOT,
    }).split(TITLE_SLOT);
    const head = parts[0] ?? '';
    const tail = parts[1] ?? '';

    const opening = head.slice(head.indexOf(MODE_SLOT) + MODE_SLOT.length);
    const start = opening ? title.indexOf(opening) : -1;
    if (start <= 0 || !title.endsWith(tail)) return title;

    return title.slice(start + opening.length, title.length - tail.length);
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate<string>(key, params) ?? '';
  }

  /**
   * Rewrites the trip as if the selected tips had been taken, and saves it
   * where `target` says — both endpoints recalculate the impact server-side:
   * - `overwrite`: over the trip itself (`PUT /trips/:id`), and the report is
   *   read back;
   * - `copy`: into a new trip (`POST /trips/:id/copy`), whose report the page
   *   then moves to. The original is left as it was.
   *
   * The trip is refetched rather than taken from `impactService.trip()`: that
   * payload carries no flight or road stops, and both endpoints build the
   * trip's collections from what they receive, so sending it would wipe every
   * itinerary.
   */
  applyTips(target: ApplyTipsTarget): void {
    const tripId = this.impactService.trip()?.id;
    const tips = this.appliedTips();
    if (!tripId || tips.length === 0 || this.isApplying()) return;

    // Read before the reload clears the selection it is computed from.
    const savings = this.formattedAvoided();

    this.isApplying.set(true);
    const rewritten$ = this.tripService.getTrip(tripId).pipe(
      map((trip) =>
        applyReductionTips(
          trip,
          tips,
          this.alternativeChoiceByTipId(),
          (transport, mode, segment) =>
            this.alternativeTitleOf(transport, mode, segment),
        ),
      ),
    );

    if (target === 'copy') {
      rewritten$
        .pipe(switchMap((trip) => this.tripService.copyTrip(tripId, trip)))
        .subscribe({
          next: ({ trip }) => {
            this.isApplying.set(false);
            this.closeApplyDialog();
            this.notificationService.success(
              this.translate('reductionTips.copied', { savings }),
            );
            // The page follows its route: it loads the copy's report and tips.
            this.router.navigate(['/home/trips', trip.id, 'impact']);
          },
          error: (error) => {
            this.isApplying.set(false);
            if (error.status === 403) {
              // The pack filled up after the count the dialog read. Once
              // refreshed, the dialog locks the copy option on its own.
              this.notificationService.error(
                this.translate('reductionTips.applyDialog.copy.limitReached'),
              );
              this.currentUserService.refreshCurrentUser();
            }
          },
        });
      return;
    }

    rewritten$
      .pipe(
        switchMap((trip) => this.tripService.updateTrip(trip)),
        // Saved figures are stale until the report is read back.
        switchMap(() => this.impactService.getDetailedImpactReport(tripId)),
      )
      .subscribe({
        next: () => {
          this.isApplying.set(false);
          this.notificationService.success(
            this.translate('reductionTips.applied', { savings }),
          );
          // Saving recreates the trip's rows, so the tips now point at ids
          // that no longer exist — they have to be asked for again. That also
          // closes the dialog.
          this.load(tripId);
        },
        error: () => this.isApplying.set(false),
      });
  }
}
